import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import AdminLogin from './components/AdminLogin';
import AdminHeader from './components/AdminHeader';
import UpcomingEventsManager from './components/UpcomingEventsManager';
import PastEventsManager from './components/PastEventsManager';
import GalleryManager from './components/GalleryManager';
import TracksManager from './components/TracksManager';
import ProfileManager from './components/ProfileManager';
import ChangePasswordModal from './components/ChangePasswordModal';
import AvailabilityManager from './components/AvailabilityManager';
import MessagesManager from './components/MessagesManager';

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'upcoming' | 'past' | 'gallery' | 'tracks' | 'availability' | 'messages'>('profile');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchProfileId = async () => {
      const { data } = await supabase.from('dj_profile').select('id').maybeSingle();
      if (data) setProfileId(data.id);
    };
    if (isAuthenticated) {
      fetchProfileId();
      fetchUnreadCount();
    }
  }, [isAuthenticated]);

  // Poll unread count every 30s
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Reset unread count when entering messages tab
  useEffect(() => {
    if (activeTab === 'messages') {
      fetchUnreadCount();
    }
  }, [activeTab]);

  const fetchUnreadCount = async () => {
    const { count } = await supabase
      .from('contact_messages')
      .select('id', { count: 'exact', head: true })
      .eq('is_read', false);
    setUnreadCount(count ?? 0);
  };

  if (!isAuthenticated) {
    return <AdminLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  const tabs: { key: typeof activeTab; label: string; icon: string }[] = [
    { key: 'profile', label: 'Perfil', icon: 'ri-user-settings-line' },
    { key: 'messages', label: 'Mensajes', icon: 'ri-mail-line' },
    { key: 'upcoming', label: 'Próximos Eventos', icon: 'ri-calendar-event-line' },
    { key: 'past', label: 'Eventos Pasados', icon: 'ri-history-line' },
    { key: 'gallery', label: 'Galería', icon: 'ri-gallery-line' },
    { key: 'tracks', label: 'Música', icon: 'ri-music-2-line' },
    { key: 'availability', label: 'Calendario', icon: 'ri-calendar-check-line' },
  ];

  return (
    <div className="min-h-screen bg-black">
      <AdminHeader
        onLogout={() => setIsAuthenticated(false)}
        onChangePassword={() => setShowPasswordModal(true)}
      />

      {showPasswordModal && (
        <ChangePasswordModal
          profileId={profileId}
          onClose={() => setShowPasswordModal(false)}
        />
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-cyan-500/20 overflow-x-auto">
          {tabs.map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`relative px-6 py-3 font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === key
                  ? 'text-cyan-400 border-b-2 border-cyan-400'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <i className={`${icon} mr-2`}></i>
              {label}
              {key === 'messages' && unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-cyan-500 text-black text-[10px] font-bold rounded-full px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div>
          {activeTab === 'profile' && <ProfileManager />}
          {activeTab === 'messages' && <MessagesManager />}
          {activeTab === 'upcoming' && <UpcomingEventsManager />}
          {activeTab === 'past' && <PastEventsManager />}
          {activeTab === 'gallery' && <GalleryManager />}
          {activeTab === 'tracks' && <TracksManager />}
          {activeTab === 'availability' && <AvailabilityManager />}
        </div>
      </div>
    </div>
  );
}
